import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { UserContext } from '../../context/UserContext';
import { apiFetch, apiJson } from '../../utils/apiClient';

export const NotificationsContext = createContext(null);

const IDLE_POLL_MS = 45000;

function lastSeenStorageKey(userId) {
    return `msvNotificationsLastSeenId_${userId}`;
}

function readLastSeenNotificationId(userId) {
    const raw = localStorage.getItem(lastSeenStorageKey(userId));
    const id = Number(raw);
    return Number.isFinite(id) && id >= 0 ? id : 0;
}

function writeLastSeenNotificationId(userId, notificationId) {
    localStorage.setItem(lastSeenStorageKey(userId), String(notificationId));
}

function getNotificationId(notification) {
    return notification?.notificationId ?? notification?.NotificationId ?? 0;
}

function maxNotificationId(notifications) {
    return notifications.reduce(
        (max, notification) => Math.max(max, getNotificationId(notification)),
        0
    );
}

function computeUnreadCount(notifications, lastSeenId) {
    return notifications.filter((notification) => getNotificationId(notification) > lastSeenId).length;
}

export function useNotifications() {
    const context = useContext(NotificationsContext);
    if (!context) {
        throw new Error('useNotifications must be used within NotificationsProvider');
    }
    return context;
}

export function NotificationsProvider({ children }) {
    const { user } = useContext(UserContext);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [clearingAll, setClearingAll] = useState(false);
    const [lastSeenId, setLastSeenId] = useState(0);
    const inFlightRef = useRef(false);
    const fetchNotificationsRef = useRef(null);

    useEffect(() => {
        if (!user?.userId) {
            setNotifications([]);
            setLastSeenId(0);
            return;
        }
        setLastSeenId(readLastSeenNotificationId(user.userId));
    }, [user?.userId]);

    const fetchNotifications = useCallback(async ({ force = false } = {}) => {
        if (!user?.userId) return [];
        if (typeof document !== 'undefined' && document.hidden) return [];
        if (inFlightRef.current && !force) return [];
        inFlightRef.current = true;
        try {
            const data = await apiJson('/Notifications/GetUserNotifications', {
                method: 'GET',
            });
            const list = Array.isArray(data) ? data : [];
            setNotifications(list);
            return list;
        } catch (error) {
            console.error('Failed to load notifications:', error);
            return [];
        } finally {
            inFlightRef.current = false;
            setLoading(false);
        }
    }, [user?.userId]);

    useEffect(() => {
        fetchNotificationsRef.current = fetchNotifications;
    }, [fetchNotifications]);

    useEffect(() => {
        if (!user?.userId) return undefined;
        setLoading(true);
        fetchNotifications();
        return undefined;
    }, [user?.userId, fetchNotifications]);

    useEffect(() => {
        if (!user?.userId) return undefined;

        let intervalId = null;

        const clearPoll = () => {
            if (intervalId != null) {
                clearInterval(intervalId);
                intervalId = null;
            }
        };

        const tick = () => {
            if (typeof document !== 'undefined' && document.hidden) return;
            fetchNotificationsRef.current?.();
        };

        const startPoll = () => {
            clearPoll();
            if (typeof document !== 'undefined' && document.hidden) return;
            intervalId = setInterval(tick, IDLE_POLL_MS);
        };

        const onVisibility = () => {
            if (document.hidden) {
                clearPoll();
            } else {
                tick();
                startPoll();
            }
        };

        startPoll();
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            clearPoll();
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [user?.userId]);

    const unreadCount = useMemo(
        () => computeUnreadCount(notifications, lastSeenId),
        [notifications, lastSeenId]
    );

    const markAllRead = useCallback(() => {
        if (!user?.userId) return;
        const nextSeenId = Math.max(lastSeenId, maxNotificationId(notifications));
        if (nextSeenId <= lastSeenId) return;
        writeLastSeenNotificationId(user.userId, nextSeenId);
        setLastSeenId(nextSeenId);
    }, [user?.userId, notifications, lastSeenId]);

    const clearNotification = useCallback(async (notificationId) => {
        if (!notificationId) return;

        setNotifications((prev) => prev.filter((item) => getNotificationId(item) !== notificationId));

        try {
            await apiFetch(`/Notifications/ClearNotification?notificationId=${notificationId}`, {
                method: 'DELETE',
            });
        } catch (error) {
            console.error(error);
            fetchNotifications({ force: true });
        }
    }, [fetchNotifications]);

    const clearAllNotifications = useCallback(async () => {
        if (notifications.length === 0 || clearingAll) return;

        setClearingAll(true);
        setNotifications([]);
        try {
            await apiFetch('/Notifications/ClearAllNotifications', {
                method: 'DELETE',
            });
        } catch (error) {
            console.error(error);
            fetchNotifications({ force: true });
        } finally {
            setClearingAll(false);
        }
    }, [notifications.length, clearingAll, fetchNotifications]);

    const value = useMemo(() => ({
        notifications,
        loading,
        unreadCount,
        clearingAll,
        fetchNotifications,
        markAllRead,
        clearNotification,
        clearAllNotifications,
    }), [
        notifications,
        loading,
        unreadCount,
        clearingAll,
        fetchNotifications,
        markAllRead,
        clearNotification,
        clearAllNotifications,
    ]);

    return (
        <NotificationsContext.Provider value={value}>
            {children}
        </NotificationsContext.Provider>
    );
}
